import React from 'react';
import { BlogPost } from '../../types/blog';
import { formatPublishDate } from '../../utils/blogUtils';

interface BlogCardProps {
  post: BlogPost;
  darkMode?: boolean;
  onClick: () => void;
}

const BlogCard: React.FC<BlogCardProps> = ({ post, darkMode = false, onClick }) => {
  const { title, shortDescription, featuredImage, publishedDate, author, tags } = post.fields || {};
  
  // Debug logging
  if (featuredImage && featuredImage.fields) {
    console.log('DEBUG: Featured image data:', featuredImage);
    console.log('DEBUG: Image URL being generated:', `https:${featuredImage.fields.file?.url}?w=400&h=200&fit=fill&f=center`);
  } else if (featuredImage) {
    console.log('DEBUG: Featured image exists but no fields:', featuredImage);
  }

  return (
    <div
      className={`p-6 rounded-lg shadow-lg cursor-pointer transition-all duration-200 hover:shadow-xl ${
        darkMode ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-white text-gray-900 hover:bg-gray-50'
      }`}
      onClick={onClick}
    >
      {/* Featured Image */}
      {featuredImage && featuredImage.fields ? (
        <div className="mb-4 overflow-hidden rounded-lg">
          <img
            src={`https:${featuredImage.fields.file?.url}?w=400&h=200&fit=fill&f=center`}
            alt={(featuredImage.fields?.title as string) || title || 'Blog post image'}
            className="w-full h-48 object-cover transition-transform duration-200 hover:scale-105"
            onError={(e) => {
              console.log('DEBUG: Image failed to load:', e.currentTarget.src);
              console.log('DEBUG: Image error event:', e);
            }}
            onLoad={() => console.log('DEBUG: Image loaded successfully:', `https:${featuredImage.fields.file?.url}`)}
          />
        </div>
      ) : featuredImage ? (
        <div className="mb-4 p-4 bg-yellow-100 text-yellow-800 rounded-lg">
          Featured image reference found but no image data loaded
        </div>
      ) : null}

      {/* Content */}
      <div className="space-y-3">
        {/* Title */}
        <h3 className="text-xl font-bold line-clamp-2">{title || 'Untitled'}</h3>

        {/* Excerpt */}
        {shortDescription && (
          <p className={`text-sm line-clamp-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {shortDescription}
          </p>
        )}

        {/* Meta Information */}
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
            {publishedDate && formatPublishDate(publishedDate)}
            {author && author.fields && <span className="ml-2">by {author.fields.name}</span>}
          </div>

          {/* Tags */}
          {tags && Array.isArray(tags) && tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {tags.slice(0, 3).map((tag: string, index: number) => (
                <span
                  key={index}
                  className={`px-2 py-1 text-xs rounded-full ${
                    darkMode
                      ? 'bg-blue-900 text-blue-200'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {tag}
                </span>
              ))}
              {tags.length > 3 && (
                <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  +{tags.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogCard;